import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Check, Copy } from '@phosphor-icons/react';
import { InviteModal } from './InviteModal';
import { BulkInviteModal } from './BulkInviteModal';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import { SAInvitesSkeleton } from './SASkeletons';

const monoFont = "'IBM Plex Mono', monospace";

interface Invite {
  id: string; token: string; email: string | null; username: string | null;
  site_name: string | null; plan: string; channel: string | null; notes: string | null;
  status: string; created_at: string; expires_at: string; opened_at: string | null;
  open_count: number; user_id: string | null;
}

type FilterStatus = 'all' | 'pending' | 'opened' | 'accepted' | 'expired' | 'cancelled';

const statusColors: Record<string, string> = { pending: 'rgba(247,246,242,0.5)', opened: '#F0E614', accepted: '#00E87A', expired: 'rgba(247,246,242,0.25)', cancelled: 'rgba(247,246,242,0.2)' };
const statusLabels: Record<string, string> = { pending: 'PENDENTE', opened: 'ABERTO', accepted: 'ACEITO', expired: 'EXPIRADO', cancelled: 'CANCELADO' };

function hoursLeft(exp: string) { const d = new Date(exp).getTime() - Date.now(); return d <= 0 ? 0 : Math.floor(d / 3600000); }
function formatOpenedAt(d: string) { const diff = Math.floor((Date.now() - new Date(d).getTime()) / 86400000); if (diff === 0) return 'hoje'; if (diff === 1) return 'ontem'; return `${diff}d atrás`; }

export function SAInvites() {
  const navigate = useNavigate();
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [showInvite, setShowInvite] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<Invite | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [usernames, setUsernames] = useState<Record<string, string>>({});

  const loadInvites = async () => {
    const { data } = await supabase.from('invites').select('*').order('created_at', { ascending: false });
    if (data) {
      const now = Date.now();
      const processed = (data as any[]).map(inv => ({
        ...inv, open_count: inv.open_count ?? 0, opened_at: inv.opened_at ?? null,
        status: (inv.status === 'pending' || inv.status === 'opened') && new Date(inv.expires_at).getTime() < now ? 'expired' : inv.status,
      }));
      setInvites(processed);
      const uids = processed.filter(i => i.user_id).map(i => i.user_id);
      if (uids.length > 0) {
        const { data: profiles } = await supabase.from('user_profiles').select('user_id, username').in('user_id', uids);
        if (profiles) { const m: Record<string, string> = {}; (profiles as any[]).forEach(p => { m[p.user_id] = p.username; }); setUsernames(m); }
      }
    }
    setLoading(false);
  };

  useEffect(() => { loadInvites(); }, []);

  const handleCopy = (inv: Invite) => {
    navigator.clipboard.writeText(`https://www.criaaa.com/convite/${inv.token}`);
    setCopiedId(inv.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    await supabase.from('invites').update({ status: 'cancelled' } as any).eq('id', cancelTarget.id);
    setCancelTarget(null); loadInvites();
  };

  const filtered = filter === 'all' ? invites : invites.filter(i => i.status === filter);

  const chipStyle = (active: boolean): React.CSSProperties => ({
    fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
    padding: '5px 10px', border: 'none', backgroundColor: active ? 'rgba(247,246,242,0.1)' : 'transparent',
    color: active ? '#F7F6F2' : 'rgba(247,246,242,0.35)', cursor: 'pointer', whiteSpace: 'nowrap',
  });

  if (loading) return <SAInvitesSkeleton />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontFamily: monoFont, fontSize: 18, fontWeight: 300, color: '#F7F6F2' }}>Convites</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowBulk(true)} style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '8px 14px', border: '0.5px solid rgba(247,246,242,0.12)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.6)', cursor: 'pointer' }}>Em massa</button>
          <button onClick={() => setShowInvite(true)} style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '8px 14px', border: '0.5px solid #00E87A', backgroundColor: 'rgba(0,232,122,0.08)', color: '#00E87A', cursor: 'pointer' }}>+ Novo</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 2, marginBottom: 16, overflowX: 'auto' }}>
        {(['all', 'pending', 'opened', 'accepted', 'expired', 'cancelled'] as FilterStatus[]).map(f => (
          <button key={f} onClick={() => setFilter(f)} style={chipStyle(filter === f)}>
            {f === 'all' ? 'Todos' : statusLabels[f]}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div style={{ fontFamily: monoFont, fontSize: 13, color: 'rgba(247,246,242,0.3)', textAlign: 'center', padding: '40px 0' }}>Nenhum convite encontrado</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(inv => {
            const isInactive = inv.status === 'cancelled' || inv.status === 'expired';
            const hrs = (inv.status === 'pending' || inv.status === 'opened') ? hoursLeft(inv.expires_at) : null;
            return (
              <div key={inv.id} style={{ backgroundColor: 'rgba(247,246,242,0.04)', border: '0.5px solid rgba(247,246,242,0.12)', padding: 14, opacity: isInactive ? 0.4 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.5)', letterSpacing: '0.05em' }}>{inv.token.substring(0, 8)}...</span>
                  <span style={{ fontFamily: monoFont, fontSize: 12, padding: '2px 6px', border: `0.5px solid ${statusColors[inv.status]}`, color: statusColors[inv.status] }}>{statusLabels[inv.status]}</span>
                </div>
                <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.45)', lineHeight: 1.8 }}>
                  {inv.plan.toUpperCase()}{inv.email ? ` · ${inv.email}` : ''}{inv.channel ? ` · ${inv.channel}` : ''}
                  {inv.open_count > 0 && inv.opened_at ? ` · Aberto ${inv.open_count}x (${formatOpenedAt(inv.opened_at)})` : ''}
                  {inv.user_id && usernames[inv.user_id] ? (
                    <> · Aceito por <span onClick={() => navigate(`/admin/usuarios/${inv.user_id}`)} style={{ color: '#00E87A', cursor: 'pointer' }}>{usernames[inv.user_id]}</span></>
                  ) : null}
                  {hrs !== null ? ` · Expira em ${hrs}h` : ''}
                </div>
                {!isInactive && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button onClick={() => handleCopy(inv)} style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '5px 10px', border: '0.5px solid #00E87A', backgroundColor: 'rgba(0,232,122,0.06)', color: '#00E87A', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                      {copiedId === inv.id ? <><Check size={10} /> Copiado</> : <><Copy size={10} /> Copiar link</>}
                    </button>
                    <button onClick={() => setCancelTarget(inv)} style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '5px 10px', border: '0.5px solid rgba(255,63,45,0.3)', backgroundColor: 'transparent', color: '#FF3F2D', cursor: 'pointer' }}>Cancelar</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog open={!!cancelTarget} onOpenChange={o => { if (!o) setCancelTarget(null); }} title="Cancelar convite" description="O link será invalidado." confirmLabel="Cancelar convite" cancelLabel="Voltar" onConfirm={handleCancel} destructive />
      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onInviteSent={() => { setShowInvite(false); loadInvites(); }} />}
      {showBulk && <BulkInviteModal onClose={() => setShowBulk(false)} onDone={() => { setShowBulk(false); loadInvites(); }} />}
    </div>
  );
}
